import { ItemKey, type ValidationIssue } from '@cyberscore/contracts';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronRight,
  Copy,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ApiError } from '../../lib/api';
import { href, navigate } from '../../lib/router';
import { useToast } from '../../ui/Toasts';
import {
  useCreateItem,
  useItem,
  useItems,
  useItemsById,
  useSaveItem,
  useQuestionInfo,
  useStudioAccess,
  useValidate,
  type ConfigItem,
} from './api';
import { ConditionEditor } from './ConditionEditor';
import { LibraryNotice } from './EditorParts';
import type { Cond } from './conditions';
import {
  addStage,
  describeWait,
  journeyProblems,
  laterStages,
  move,
  moveStage,
  pickVariant,
  relevantIssues,
  removeStage,
  sameJson,
  setPrerequisite,
  stageColumns,
  type Journey,
  type Stage,
} from './journey';
import { RoutesEditor } from './RoutesEditor';
import { SCOPE_LABEL } from './StudioPage';
import { TryFlow } from './TryFlow';

/**
 * The journey builder (task 41): the stages of a journey, what each waits for, and what each asks. Edits
 * stay in the browser until saved as drafts; publishing is a release a second person approves.
 */
export function JourneyBuilder({ id }: { id: string }) {
  const access = useStudioAccess();
  const item = useItem(id);
  const stageList = useItems('stage');
  const questionList = useItems('question');
  const save = useSaveItem();
  const create = useCreateItem();
  const validate = useValidate();
  const toast = useToast();

  const [journey, setJourney] = useState<Journey | null>(null);
  const [stageEdits, setStageEdits] = useState<Record<string, Stage>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<ValidationIssue[] | null>(null);

  // A fresh copy of what is stored whenever another revision loads (adjusted while rendering, not in an effect).
  const stamp = item.data ? `${item.data.id}:${item.data.revision}` : null;
  const [loaded, setLoaded] = useState<string | null>(null);
  if (stamp && stamp !== loaded) {
    setLoaded(stamp);
    setJourney(structuredClone(item.data!.body) as Journey);
  }

  const keys = journey?.stages.map((s) => s.stage) ?? [];
  const variants = keys.map((k) => pickVariant(stageList.data ?? [], k, access.own));
  const stageItems = useItemsById(variants.filter((v) => !!v).map((v) => v!.id));
  const stored: Record<string, ConfigItem> = {};
  for (const q of stageItems) if (q.data) stored[q.data.key] = q.data;
  const bodies: Record<string, Stage> = {};
  for (const k of keys) {
    const b = stageEdits[k] ?? (stored[k]?.body as Stage | undefined);
    if (b) bodies[k] = b;
  }
  const questionTitles = useMemo(
    () => new Map((questionList.data ?? []).map((q) => [q.key, q.title])),
    [questionList.data],
  );
  const questionKeys = useMemo(() => new Set(questionTitles.keys()), [questionTitles]);

  if (item.isPending || !journey) return <div className="empty">Loading…</div>;
  if (item.isError) return <div className="empty">{item.error.message}</div>;

  const it = item.data;
  const ownsJourney = access.edits && access.own.includes(it.scope_level);
  const ownsStage = (k: string) => access.edits && !!stored[k] && access.own.includes(stored[k]!.scope_level);
  const journeyDirty = !sameJson(journey, it.body);
  const dirtyStages = Object.keys(stageEdits).filter(
    (k) => stored[k] && !sameJson(stageEdits[k], stored[k]!.body),
  );
  const dirty = journeyDirty || dirtyStages.length > 0;
  const loadingStages = stageItems.some((q) => q.isPending);
  const problems = loadingStages ? [] : journeyProblems(journey, bodies, questionKeys);
  const name = (k: string) => bodies[k]?.name ?? k;
  const sel = selected && keys.includes(selected) ? selected : keys[0]!;

  const editStage = (k: string, change: (s: Stage) => Stage) =>
    setStageEdits((e) => ({ ...e, [k]: change(structuredClone(e[k] ?? (stored[k]!.body as Stage))) }));

  const saveAll = async () => {
    try {
      if (journeyDirty) await save.mutateAsync({ id: it.id, body: journey, revision: it.revision });
      for (const k of dirtyStages) {
        const row = stored[k]!;
        await save.mutateAsync({ id: row.id, body: stageEdits[k], revision: row.revision });
        setStageEdits(({ [k]: _, ...rest }) => rest);
      }
      setServerIssues(null);
      toast('Draft saved. Submit a release to publish it.', 'ok');
    } catch (e) {
      toast(
        e instanceof ApiError && e.status === 409
          ? 'Someone else saved this since you opened it. Reload to see their version (your unsaved edits will be lost).'
          : e instanceof ApiError && e.issues.length
            ? `Not saved: ${e.issues.map((i) => `${i.path} ${i.message}`).join('; ')}`
            : e instanceof Error ? e.message : 'Could not save',
        'bad',
      ); // prettier-ignore
    }
  };

  /** Copies an inherited journey or stage into the author's own scope, where it can be changed. */
  const customise = async (kind: 'journey' | 'stage', key: string, body: unknown) => {
    try {
      const row = await create.mutateAsync({ kind, key, body, note: 'Customised from the platform version' });
      if (kind === 'journey') navigate(href.journey(row.id));
      toast(`${key} is now your own copy`, 'ok');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not copy it', 'bad');
    }
  };

  const check = async () => {
    try {
      const r = await validate.mutateAsync();
      setServerIssues(relevantIssues(r.issues, it.key, keys));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not validate', 'bad');
    }
  };

  return (
    <>
      <div className="crumbs">
        <a href={href.studio()}>Studio</a>
        <ChevronRight size={14} />
        {journey.name || it.key}
      </div>
      <div className="page-head row-between">
        <div>
          <h1>{journey.name || it.key}</h1>
          <p>
            <span className="mono">{it.key}</span> · <span className="pill neutral">{SCOPE_LABEL[it.scope_level] ?? it.scope_level}</span> · revision {it.revision}
            {dirty && <span className="pill warn-pill ml">Unsaved changes</span>}
          </p>
        </div>
        <div className="row gap">
          <button type="button" className="btn" onClick={check} disabled={validate.isPending}>
            <ShieldCheck size={16} /> {validate.isPending ? 'Checking…' : 'Check'}
          </button>
          {ownsJourney ? (
            <button type="button" className="btn btn-primary" onClick={saveAll} disabled={!dirty || save.isPending}>
              <Save size={16} /> {save.isPending ? 'Saving…' : 'Save draft'}
            </button>
          ) : (
            access.edits && (
              <button type="button" className="btn btn-primary" onClick={() => customise('journey', it.key, it.body)} disabled={create.isPending}>
                <Copy size={16} /> Customise for your organisation
              </button>
            )
          )}
        </div>
      </div>

      <LibraryNotice item={it} draft={journey} editable={ownsJourney} onLoad={(b) => setJourney(b as Journey)} />
      {!ownsJourney && (
        <div className="notice neutral mb">
          {access.edits
            ? 'This journey comes from the platform. Customise it to change it for your organisation; the platform version stays as it is.'
            : 'You can look, but only configuration editors change journeys.'}
        </div>
      )}

      <div className="builder">
        <div className="stack">
          <section className="card pad">
            <div className="two">
              <div className="field">
                <label htmlFor="j-name">Journey name</label>
                <input id="j-name" className="input" value={journey.name} disabled={!ownsJourney} onChange={(e) => setJourney({ ...journey, name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="j-desc">Description</label>
                <input id="j-desc" className="input" value={journey.description} disabled={!ownsJourney} onChange={(e) => setJourney({ ...journey, description: e.target.value })} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="j-coverage">Answered for a reliable score (% of the controls that apply)</label>
              <input id="j-coverage" className="input input-sm" type="number" min={30} max={100} placeholder="80" value={journey.coverage === undefined ? '' : Math.round(journey.coverage * 100)} disabled={!ownsJourney} onChange={(e) => setJourney({ ...journey, coverage: e.target.value ? Math.min(100, Math.max(30, Number(e.target.value))) / 100 : undefined })} />
              <span className="muted small">Past it, the questions left are offered as optional: the business may stop or carry on.</span>
            </div>
          </section>

          <section className="card pad" aria-labelledby="flow-h">
            <h2 id="flow-h" className="section-title">How the stages open</h2>
            <p className="muted small">Stages in the same column open together. Select one to edit it.</p>
            <div className="flow-cols">
              {stageColumns(journey).map((col, i) => (
                <div key={i} className="flow-col">
                  {col.map((k) => (
                    <button key={k} type="button" className={`flow-node${k === sel ? ' on' : ''}${problems.some((p) => p.where === k) ? ' bad' : ''}`} aria-pressed={k === sel} aria-label={`${name(k)} (${k})`} onClick={() => setSelected(k)}>
                      <b>{name(k)}</b>
                      <span className="muted small">
                        {k} · {bodies[k]?.questions.length ?? '…'} questions
                        {bodies[k] && !bodies[k]!.mandatory ? ' · optional' : ''}
                        {bodies[k]?.mode === 'conditional' ? ' · conditional' : ''}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section className="card" aria-labelledby="stages-h">
            <div className="card-head">
              <h2 id="stages-h" className="card-title">Stages, in order</h2>
            </div>
            <ol className="stage-rows">
              {journey.stages.map((s, i) => (
                <li key={s.stage} className={s.stage === sel ? 'on' : undefined}>
                  <button type="button" className="linklike grow left" aria-label={`Edit ${name(s.stage)}: ${describeWait(s, name)}`} onClick={() => setSelected(s.stage)}>
                    <b>{name(s.stage)}</b>
                    <span className="muted small">{describeWait(s, name)}</span>
                  </button>
                  {ownsJourney && (
                    <span className="row">
                      <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Move ${s.stage} up`} disabled={i === 0} onClick={() => setJourney(moveStage(journey, i, i - 1))}>
                        <ArrowUp size={14} />
                      </button>
                      <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Move ${s.stage} down`} disabled={i === journey.stages.length - 1} onClick={() => setJourney(moveStage(journey, i, i + 1))}>
                        <ArrowDown size={14} />
                      </button>
                      <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Remove ${s.stage} from the journey`} disabled={journey.stages.length === 1} onClick={() => setJourney(removeStage(journey, s.stage))}>
                        <Trash2 size={14} />
                      </button>
                    </span>
                  )}
                </li>
              ))}
            </ol>
            {ownsJourney && <AddStage journey={journey} stages={stageList.data ?? []} onAdd={(k) => { setJourney(addStage(journey, k)); setSelected(k); }} />}
          </section>

          <TryFlow journeyKey={it.key} edits={{ ...(journeyDirty ? { journey } : {}), stages: Object.fromEntries(dirtyStages.map((k) => [k, stageEdits[k]])) }} />
        </div>

        <aside className="stack">
          <Problems problems={problems} server={serverIssues} name={name} />
          {stored[sel] && (
            <LibraryNotice item={stored[sel]!} draft={bodies[sel]} editable={ownsStage(sel)} onLoad={(b) => editStage(sel, () => b as Stage)} />
          )}
          {bodies[sel] ? (
            <StageEditor
              key={sel}
              stageKey={sel}
              stage={bodies[sel]!}
              journey={journey}
              name={name}
              editable={ownsStage(sel)}
              journeyEditable={ownsJourney}
              inherited={!!stored[sel] && !access.own.includes(stored[sel]!.scope_level)}
              canCustomise={access.edits}
              onCustomise={() => customise('stage', sel, stored[sel]!.body)}
              onChange={(f) => editStage(sel, f)}
              onWait={(on, when) => setJourney(setPrerequisite(journey, sel, on, when))}
              questions={questionList.data ?? []}
              titles={questionTitles}
              askedElsewhere={(q) => keys.find((k) => k !== sel && bodies[k]?.questions.includes(q))}
            />
          ) : (
            <section className="card pad muted small">{loadingStages ? 'Loading the stage…' : `No stage ${sel} exists.`}</section>
          )}
        </aside>
      </div>
    </>
  ); // prettier-ignore
}

function Problems({
  problems,
  server,
  name,
}: {
  problems: { where: string; message: string }[];
  server: ValidationIssue[] | null;
  name: (k: string) => string;
}) {
  if (!problems.length && server === null) return null;
  const all = [
    ...problems.map((p) => ({
      where: p.where === 'journey' ? 'Journey' : name(p.where),
      message: p.message,
    })),
    ...(server ?? []).map((i) => ({
      where: i.kind === 'journey' ? 'Journey' : name(i.key),
      message: i.message,
    })),
  ];
  return (
    <section className={`card pad ${all.length ? 'problems' : ''}`} aria-live="polite">
      {all.length ? (
        <>
          <div className="eyebrow row gap">
            <AlertTriangle size={14} className="warn-t" /> To fix before releasing ({all.length})
          </div>
          <ul className="problem-list">
            {all.map((p, i) => (
              <li key={i}>
                <b>{p.where}:</b> {p.message}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="ok-line">
          <CheckCircle2 size={16} className="ok-t" /> The saved drafts pass every check.
        </div>
      )}
    </section>
  );
}

function AddStage({
  journey,
  stages,
  onAdd,
}: {
  journey: Journey;
  stages: { key: string; title: string }[];
  onAdd: (key: string) => void;
}) {
  const create = useCreateItem();
  const toast = useToast();
  const [pick, setPick] = useState('');
  const [fresh, setFresh] = useState({ key: '', name: '' });
  const free = [...new Map(stages.filter((s) => !journey.stages.some((j) => j.stage === s.key)).map((s) => [s.key, s])).values()]; // prettier-ignore
  return (
    <div className="add-stage">
      <div className="input-row">
        <select className="input" aria-label="Stage to add" value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">Add an existing stage…</option>
          {free.map((s) => (
            <option key={s.key} value={s.key}>
              {s.key} · {s.title}
            </option>
          ))}
        </select>
        <button type="button" className="btn" disabled={!pick} onClick={() => { onAdd(pick); setPick(''); }}>
          <Plus size={14} /> Add
        </button>
      </div>
      <form
        className="input-row"
        onSubmit={async (e) => {
          e.preventDefault();
          const key = ItemKey.safeParse(fresh.key);
          if (!key.success || !fresh.name.trim()) return toast('A new stage needs a key (letters, digits, _ . -) and a name', 'bad');
          try {
            await create.mutateAsync({ kind: 'stage', key: fresh.key, body: { name: fresh.name.trim() } });
            onAdd(fresh.key);
            setFresh({ key: '', name: '' });
          } catch (err) {
            toast(err instanceof Error ? err.message : 'Could not create the stage', 'bad');
          }
        }}
      >
        <input className="input mono key-input" aria-label="New stage key" placeholder="S09" value={fresh.key} onChange={(e) => setFresh({ ...fresh, key: e.target.value })} />
        <input className="input" aria-label="New stage name" placeholder="New stage name" value={fresh.name} onChange={(e) => setFresh({ ...fresh, name: e.target.value })} />
        <button type="submit" className="btn" disabled={create.isPending}>
          <Plus size={14} /> New stage
        </button>
      </form>
    </div>
  ); // prettier-ignore
}

function StageEditor({
  stageKey,
  stage,
  journey,
  name,
  editable,
  journeyEditable,
  inherited,
  canCustomise,
  onCustomise,
  onChange,
  onWait,
  questions,
  titles,
  askedElsewhere,
}: {
  stageKey: string;
  stage: Stage;
  journey: Journey;
  name: (k: string) => string;
  editable: boolean;
  journeyEditable: boolean;
  inherited: boolean;
  canCustomise: boolean;
  onCustomise: () => void;
  onChange: (f: (s: Stage) => Stage) => void;
  onWait: (on: string, when: 'completed' | 'started' | null) => void;
  questions: { key: string; title: string }[];
  titles: Map<string, string>;
  askedElsewhere: (q: string) => string | undefined;
}) {
  const [search, setSearch] = useState('');
  const questionInfo = useQuestionInfo();
  const idx = journey.stages.findIndex((s) => s.stage === stageKey);
  const earlier = journey.stages.slice(0, idx);
  const waits = journey.stages[idx]!.after;
  const set = <K extends keyof Stage>(k: K, v: Stage[K]) => onChange((s) => ({ ...s, [k]: v }));
  const found = search.trim().length < 2 ? [] : [...new Map(questions.filter((q) => !stage.questions.includes(q.key) && `${q.key} ${q.title}`.toLowerCase().includes(search.toLowerCase())).map((q) => [q.key, q])).values()].slice(0, 8); // prettier-ignore

  return (
    <section className="card pad stage-editor" aria-label={`Stage ${stageKey}`}>
      <div className="row-between">
        <div className="eyebrow">Stage {stageKey}</div>
        {inherited && <span className="pill neutral">Platform</span>}
      </div>
      {inherited && canCustomise && (
        <div className="notice neutral">
          This stage comes from the platform.{' '}
          <button type="button" className="linklike" onClick={onCustomise}>
            Customise it for your organisation
          </button>{' '}
          to change it.
        </div>
      )}
      <fieldset disabled={!editable} className="plain stack-sm">
        <div className="field">
          <label htmlFor="s-name">Name</label>
          <input id="s-name" className="input" value={stage.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="s-desc">Description</label>
          <textarea id="s-desc" className="input" rows={2} value={stage.description} onChange={(e) => set('description', e.target.value)} />
        </div>
        <div className="two">
          <div className="field">
            <label htmlFor="s-mode">Shown as</label>
            <select id="s-mode" className="input" value={stage.mode} onChange={(e) => set('mode', e.target.value as Stage['mode'])}>
              <option value="sequential">Sequential</option>
              <option value="parallel">Parallel</option>
              <option value="conditional">Conditional</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="s-order">Question order</label>
            <select id="s-order" className="input" value={stage.order} onChange={(e) => set('order', e.target.value as Stage['order'])}>
              <option value="configured">As listed</option>
              <option value="ranked">Most valuable first</option>
            </select>
          </div>
        </div>
        <label className="toggle-row">
          <input type="checkbox" checked={stage.mandatory} onChange={(e) => set('mandatory', e.target.checked)} /> Must be completed for the journey to finish
        </label>
        <div className="two">
          <div className="field">
            <label htmlFor="s-mins">Minutes (estimate)</label>
            <input id="s-mins" className="input" type="number" min={1} value={stage.estimated_minutes ?? ''} onChange={(e) => set('estimated_minutes', e.target.value ? Math.max(1, Math.round(Number(e.target.value))) : undefined)} />
          </div>
          <div className="field">
            <label htmlFor="s-done">When it is done, say</label>
            <input id="s-done" className="input" value={stage.completion_message} onChange={(e) => set('completion_message', e.target.value)} />
          </div>
        </div>
        <div className="field">
          <span className="field-label">Entered only when{stage.mode === 'conditional' ? ' (required for a conditional stage)' : ''}</span>
          <ConditionEditor value={stage.entry as Cond | undefined} onChange={(v) => set('entry', v as Stage['entry'])} questions={questionInfo} mode="question" disabled={!editable} emptyLabel="Always entered once it opens" />
        </div>
      </fieldset>

      <div className="field">
        <span className="field-label">Opens after</span>
        {earlier.length ? (
          <ul className="wait-list">
            {earlier.map((e) => {
              const w = waits.find((a) => a.stage === e.stage);
              return (
                <li key={e.stage}>
                  <span className="grow">{name(e.stage)}</span>
                  <select className="input input-sm" aria-label={`Wait for ${e.stage}`} disabled={!journeyEditable} value={w?.when ?? ''} onChange={(ev) => onWait(e.stage, (ev.target.value || null) as 'completed' | 'started' | null)}>
                    <option value="">Doesn’t wait</option>
                    <option value="completed">Is completed</option>
                    <option value="started">Has started</option>
                  </select>
                </li>
              );
            })}
          </ul>
        ) : (
          <span className="muted small">The first stage starts straight away.</span>
        )}
      </div>

      <div className="field">
        <span className="field-label">Questions ({stage.questions.length})</span>
        <ol className="q-rows">
          {stage.questions.map((q, i) => {
            const other = askedElsewhere(q);
            return (
              <li key={q}>
                <span className="mono small">{q}</span>
                <span className="grow small">
                  {titles.get(q) ?? <span className="bad-t">Unknown question</span>}
                  {other && <span className="warn-t"> · also in {other}</span>}
                </span>
                {editable && (
                  <span className="row">
                    <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Move ${q} up`} disabled={i === 0} onClick={() => set('questions', move(stage.questions, i, i - 1))}>
                      <ArrowUp size={13} />
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Move ${q} down`} disabled={i === stage.questions.length - 1} onClick={() => set('questions', move(stage.questions, i, i + 1))}>
                      <ArrowDown size={13} />
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Remove ${q}`} onClick={() => set('questions', stage.questions.filter((x) => x !== q))}>
                      <Trash2 size={13} />
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
        {editable && (
          <>
            <input className="input" aria-label="Find a question to add" placeholder="Find a question to add (key or words)" value={search} onChange={(e) => setSearch(e.target.value)} />
            {found.length > 0 && (
              <ul className="q-found">
                {found.map((q) => (
                  <li key={q.key}>
                    <button type="button" className="linklike left" onClick={() => { set('questions', [...stage.questions, q.key]); setSearch(''); }}>
                      <span className="mono small">{q.key}</span> {q.title}
                      {askedElsewhere(q.key) && <span className="warn-t small"> · in {askedElsewhere(q.key)}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <RoutesEditor
        stageKey={stageKey}
        questions={stage.questions}
        routes={stage.routes ?? []}
        later={laterStages(journey, stageKey)}
        name={name}
        info={questionInfo}
        titles={titles}
        editable={editable}
        onChange={(routes) => set('routes', routes)}
      />
    </section>
  ); // prettier-ignore
}
